import { useState, useRef, useCallback } from 'react';
import type { FormConfiguration, FormQuestion, FormFieldType } from '@/types';

export type FormAnswers = Record<string, unknown>;

const CA_EPI_EXAMPLE_IMAGE = '/assets/ca-epi-exemplo.png';

function getOtherAnswerId(questionId: string) {
  return `${questionId}-outro`;
}

export function isOtherOption(option: string) {
  return option.trim().toLowerCase().startsWith('outro');
}

export function resolveOtherAnswer(answers: FormAnswers, questionId: string) {
  const value = ((answers[questionId] as string) || '').trim();
  const otherValue = ((answers[getOtherAnswerId(questionId)] as string) || '').trim();

  if (value && isOtherOption(value) && otherValue) {
    return `${value} - ${otherValue}`;
  }

  return value;
}

interface ConfigurableFormProps {
  config: FormConfiguration;
  initialValues?: FormAnswers;
  onSubmit: (answers: FormAnswers) => void;
  submitLabel?: string;
  cancelLabel?: string;
  onCancel?: () => void;
  disabled?: boolean;
  onAnswersChange?: (answers: FormAnswers) => void;
}

function generateInitialValues(questions: FormQuestion[]): FormAnswers {
  const values: FormAnswers = {};
  questions.forEach((q) => {
    if (q.type === 'tabela') {
      const rows: Record<string, Record<string, boolean>> = {};
      (q.tableRows || []).forEach((row) => {
        const cols: Record<string, boolean> = {};
        (q.tableHeaders || []).forEach((h) => { cols[h] = false; });
        rows[row] = cols;
      });
      values[q.id] = rows;
    } else if (q.type === 'checkbox') {
      values[q.id] = false;
    } else if (q.type === 'numero') {
      values[q.id] = '';
    } else {
      values[q.id] = '';
    }
  });
  return values;
}

export default function ConfigurableForm({
  config,
  initialValues,
  onSubmit,
  submitLabel = 'Enviar',
  cancelLabel = 'Limpar formulário',
  onCancel,
  disabled = false,
  onAnswersChange,
}: ConfigurableFormProps) {
  const [answers, setAnswers] = useState<FormAnswers>(() => {
    const init = generateInitialValues(config.questions);
    return { ...init, ...initialValues };
  });

  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [files, setFiles] = useState<Record<string, File | null>>({});

  const sortedQuestions = [...config.questions].sort((a, b) => a.order - b.order);

  const updateAnswers = useCallback((updater: (prev: FormAnswers) => FormAnswers) => {
    setAnswers((prev) => {
      const next = updater(prev);
      onAnswersChange?.(next);
      return next;
    });
  }, [onAnswersChange]);

  const setValue = useCallback((id: string, value: unknown) => {
    updateAnswers((prev) => ({ ...prev, [id]: value }));
  }, [updateAnswers]);

  const handleMultipleChoiceChange = (questionId: string, option: string) => {
    updateAnswers((prev) => ({
      ...prev,
      [questionId]: option,
      ...(isOtherOption(option) ?{} : { [getOtherAnswerId(questionId)]: '' }),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled) return;
    onSubmit({ ...answers, ...files });
  };

  const handleTableChange = (questionId: string, row: string, col: string) => {
    updateAnswers((prev) => {
      const current = (prev[questionId] as Record<string, Record<string, boolean>>) || {};
      const rowData = { ...(current[row] || {}) };
      rowData[col] = !rowData[col];
      return { ...prev, [questionId]: { ...current, [row]: rowData } };
    });
  };

  const handleFileChange = (questionId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFiles((prev) => ({ ...prev, [questionId]: file }));
      updateAnswers((prev) => ({ ...prev, [questionId]: file }));
    }
  };

  const removeFile = (questionId: string) => {
    setFiles((prev) => ({ ...prev, [questionId]: null }));
    updateAnswers((prev) => ({ ...prev, [questionId]: null }));
    const ref = fileInputRefs.current[questionId];
    if (ref) ref.value = '';
  };

  const renderField = (q: FormQuestion) => {
    const value = answers[q.id];

    switch (q.type) {
      case 'texto_curto':
        return (
          <input
            type="text"
            value={value as string}
            onChange={(e) => setValue(q.id, e.target.value)}
            placeholder="Sua resposta"
            required={q.required}
            className="w-full px-3 py-2.5 border-b-2 border-portal-gray-200 focus:border-portal-purple focus:outline-none text-sm text-portal-gray-800 placeholder-portal-gray-400 transition-colors bg-transparent"
          />
        );

      case 'texto_longo':
        return (
          <>
            <textarea
              value={value as string}
              onChange={(e) => setValue(q.id, e.target.value)}
              placeholder="Sua resposta"
              rows={4}
              maxLength={500}
              required={q.required}
              className="w-full px-3 py-2.5 border-b-2 border-portal-gray-200 focus:border-portal-purple focus:outline-none text-sm text-portal-gray-800 placeholder-portal-gray-400 transition-colors bg-transparent resize-none"
            />
            <p className="text-xs text-portal-gray-400 mt-1 text-right">{(value as string)?.length || 0}/500</p>
          </>
        );

      case 'numero':
        return (
          <input
            type="number"
            value={value as string}
            onChange={(e) => setValue(q.id, e.target.value)}
            placeholder="0"
            required={q.required}
            className="w-32 px-3 py-2.5 border-b-2 border-portal-gray-200 focus:border-portal-purple focus:outline-none text-sm text-portal-gray-800 placeholder-portal-gray-400 transition-colors bg-transparent"
          />
        );

      case 'data':
        return (
          <input
            type="date"
            value={value as string}
            onChange={(e) => setValue(q.id, e.target.value)}
            required={q.required}
            className="w-full px-3 py-2.5 border-b-2 border-portal-gray-200 focus:border-portal-purple focus:outline-none text-sm text-portal-gray-800 transition-colors bg-transparent"
          />
        );

      case 'multipla_escolha':
        return (
          <div className="space-y-2">
            {(q.options || []).map((opt) => {
              const isSelectedOther = value === opt && isOtherOption(opt);
              const otherAnswerId = getOtherAnswerId(q.id);

              return (
                <div key={opt} className="space-y-2">
                  <label className="flex items-center gap-2.5 cursor-pointer group">
                    <input
                      type="radio"
                      name={q.id}
                      value={opt}
                      checked={value === opt}
                      onChange={() => handleMultipleChoiceChange(q.id, opt)}
                      required={q.required}
                      className="w-4 h-4 accent-portal-purple"
                    />
                    <span className="text-sm text-portal-gray-700">{opt}</span>
                  </label>
                  {isSelectedOther && (
                    <input
                      type="text"
                      value={(answers[otherAnswerId] as string) || ''}
                      onChange={(e) => setValue(otherAnswerId, e.target.value)}
                      placeholder="Especifique"
                      required={q.required}
                      autoFocus
                      className="ml-6 w-full max-w-md px-3 py-2 border-b-2 border-portal-gray-200 focus:border-portal-purple focus:outline-none text-sm text-portal-gray-800 placeholder-portal-gray-400 transition-colors bg-transparent"
                    />
                  )}
                </div>
              );
            })}
            {(q.options || []).length === 0 && (
              <p className="text-sm text-portal-gray-400 italic">Nenhuma opção configurada</p>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => setValue(q.id, e.target.checked)}
              required={q.required}
              className="w-4 h-4 accent-portal-purple mt-0.5"
            />
            <span className="text-sm text-portal-gray-700">{q.label}</span>
          </label>
        );

      case 'tabela': {
        const tableData = (value as Record<string, Record<string, boolean>>) || {};
        const headers = q.tableHeaders || [];
        const rows = q.tableRows || [];
        return (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-portal-gray-200">
                  <th className="text-left py-2 px-2 font-medium text-portal-gray-600"></th>
                  {headers.map((h) => (
                    <th key={h} className="text-center py-2 px-2 font-medium text-portal-gray-600 w-16">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row} className="border-b border-portal-gray-100">
                    <td className="py-2 px-2 text-portal-gray-700">
                      {row}
                      {row === 'Outro' && (
                        <input
                          type="text"
                          value={(answers[`${q.id}-outro`] as string) || ''}
                          onChange={(e) => setValue(`${q.id}-outro`, e.target.value)}
                          placeholder="Qual?"
                          className="ml-2 px-2 py-1 text-xs border-b border-portal-gray-300 focus:border-portal-purple focus:outline-none bg-transparent w-32"
                        />
                      )}
                    </td>
                    {headers.map((col) => (
                      <td key={col} className="text-center py-2 px-2">
                        <input
                          type="checkbox"
                          checked={tableData[row]?.[col] || false}
                          onChange={() => handleTableChange(q.id, row, col)}
                          className="w-4 h-4 accent-portal-purple rounded"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }

      case 'upload':
        return (
          <div>
            <input
              ref={(el) => { fileInputRefs.current[q.id] = el; }}
              type="file"
              accept="image/*,.pdf,.doc,.docx"
              onChange={(e) => handleFileChange(q.id, e)}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRefs.current[q.id]?.click()}
              className="flex items-center gap-2 px-4 py-2.5 border border-portal-gray-300 rounded-lg text-sm text-portal-gray-600 hover:bg-portal-gray-50 transition-colors"
            >
              <i className="ri-upload-cloud-line"></i>
              {files[q.id]?.name || 'Adicionar arquivo'}
            </button>
            {files[q.id] && (
              <button
                type="button"
                onClick={() => removeFile(q.id)}
                className="text-xs text-red-500 mt-2 hover:text-red-600"
              >
                Remover arquivo
              </button>
            )}
          </div>
        );

      default:
        return (
          <p className="text-sm text-portal-gray-400">Tipo de campo não suportado: {q.type}</p>
        );
    }
  };

  const isLabelVisible = (type: FormFieldType) => type !== 'checkbox';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {sortedQuestions.map((q) => (
        <div key={q.id} className="bg-white rounded-xl border border-portal-gray-200 p-6">
          {isLabelVisible(q.type) && (
            <label className="block text-sm font-medium text-portal-gray-800 mb-3">
              {q.label}
              {q.required && <span className="text-red-500 ml-1">*</span>}
            </label>
          )}
          {q.id === 'q-den-1' && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
              Caso queira anonimato, todos os dados estarão ocultos e protegidos.
            </div>
          )}
          {q.id === 'q-epi-5' && (
            <img
              src={CA_EPI_EXAMPLE_IMAGE}
              alt="Exemplo de onde encontrar o CA do EPI"
              className="mb-4 w-full max-w-md rounded-lg border border-portal-gray-200 object-contain"
            />
          )}
          {renderField(q)}
        </div>
      ))}

      <div className="flex items-center justify-between pt-2">
        <button
          type="submit"
          disabled={disabled}
          className="px-6 py-2.5 bg-portal-purple text-white text-sm font-medium rounded-lg hover:bg-portal-purple-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {submitLabel}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="text-sm text-portal-purple hover:text-portal-purple-dark font-medium transition-colors whitespace-nowrap"
          >
            {cancelLabel}
          </button>
        )}
      </div>
    </form>
  );
}
